/*
  File: assets/ozplug-main-product.js
  Purpose: Product media, variants, quantity, price updates, and AJAX add-to-cart
*/

(function () {
  'use strict';

  if (window.OZPlugMainProductReady) return;
  window.OZPlugMainProductReady = true;

  const ROOT_SELECTOR = "[data-oz-pdp]";
  const INIT_ATTRIBUTE = "data-oz-pdp-initialized";

  function initSection(root) {
    if (!root || root.hasAttribute(INIT_ATTRIBUTE)) return;

    root.setAttribute(INIT_ATTRIBUTE, "true");

    const variantDataElement = root.querySelector("[data-oz-pdp-variants]");
    const variants = parseVariants(variantDataElement);
    const state = {
      variants: variants,
      selectedVariant:
        variants.find((variant) => String(variant.id) === String(root.dataset.selectedVariantId)) ||
        variants[0] ||
        null
    };

    initMedia(root);
    initOptions(root, state);
    initQuantity(root, state);
    initForm(root, state);
    initRelatedForms(root);

    updateProductUI(root, state);
  }

  function parseVariants(element) {
    if (!element) return [];

    try {
      return JSON.parse(element.textContent || "[]");
    } catch (error) {
      console.warn("OZPDP: Could not parse product variants.", error);
      return [];
    }
  }

  function initMedia(root) {
    const slides = Array.from(root.querySelectorAll("[data-oz-pdp-media-slide]"));
    const thumbs = Array.from(root.querySelectorAll("[data-oz-pdp-thumb]"));

    function playSelectedVideo(slide) {
      const video = slide?.querySelector("video");
      if (!video || typeof video.play !== "function") return;

      // Muted playback keeps click-to-preview reliable across modern browsers.
      video.muted = true;
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");

      try {
        video.currentTime = 0;
      } catch (error) {
        // Some streams do not allow seeking before metadata is ready.
      }

      try {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            // Keep native controls visible if browser policy blocks autoplay.
          });
        }
      } catch (error) {
        // Keep native controls visible if playback cannot start automatically.
      }
    }

    function setActiveMedia(mediaId, shouldPlayVideo = false) {
      slides.forEach((slide) => {
        const active = String(slide.dataset.mediaId) === String(mediaId);
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", active ? "false" : "true");

        if (active && shouldPlayVideo) {
          playSelectedVideo(slide);
          return;
        }

        if (!active) {
          slide.querySelectorAll("video").forEach((video) => {
            if (typeof video.pause === "function") {
              video.pause();
            }

            try {
              video.currentTime = 0;
            } catch (error) {
              // Ignore streams that do not support resetting.
            }
          });
        }
      });

      thumbs.forEach((thumb) => {
        thumb.classList.toggle(
          "is-active",
          String(thumb.dataset.mediaId) === String(mediaId)
        );
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        setActiveMedia(thumb.dataset.mediaId, true);
      });
    });

    root.ozPdpSetActiveMedia = (mediaId) => setActiveMedia(mediaId, false);
  }

  function initOptions(root, state) {
    const optionInputs = Array.from(
      root.querySelectorAll("[data-oz-pdp-option-input]")
    );

    if (!optionInputs.length) return;

    optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        const selectedOptions = getSelectedOptions(root);
        const matchingVariant = findVariantByOptions(state.variants, selectedOptions);

        state.selectedVariant = matchingVariant || null;
        updateProductUI(root, state);
      });
    });
  }

  function getSelectedOptions(root) {
    const selectedOptions = [];

    root
      .querySelectorAll("[data-oz-pdp-option-group]")
      .forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const checked = group.querySelector("[data-oz-pdp-option-input]:checked");

        selectedOptions[index] = checked ? checked.value : "";
      });

    return selectedOptions;
  }

  function findVariantByOptions(variants, selectedOptions) {
    return variants.find((variant) => {
      return selectedOptions.every((option, index) => {
        return String(variant.options[index]) === String(option);
      });
    });
  }

  function initQuantity(root, state) {
    const quantityInput = root.querySelector("[data-oz-pdp-qty]");
    const minus = root.querySelector("[data-oz-pdp-qty-minus]");
    const plus = root.querySelector("[data-oz-pdp-qty-plus]");

    if (!quantityInput) return;

    minus?.addEventListener("click", () => {
      const value = Math.max(1, getQuantity(root) - 1);
      quantityInput.value = String(value);
      updateProductUI(root, state);
    });

    plus?.addEventListener("click", () => {
      const value = getQuantity(root) + 1;
      quantityInput.value = String(value);
      updateProductUI(root, state);
    });

    quantityInput.addEventListener("input", () => {
      quantityInput.value = String(Math.max(1, getQuantity(root)));
      updateProductUI(root, state);
    });
  }

  function getQuantity(root) {
    const quantityInput = root.querySelector("[data-oz-pdp-qty]");
    const value = Number(quantityInput?.value || 1);

    if (!Number.isFinite(value) || value < 1) return 1;

    return Math.floor(value);
  }

  function initForm(root, state) {
    const form = root.querySelector("[data-oz-pdp-form]");
    const button = root.querySelector("[data-oz-pdp-add-button]");
    const label = root.querySelector("[data-oz-pdp-add-label]");
    const errorElement = root.querySelector("[data-oz-pdp-error]");

    if (!form || !button) return;

    form.addEventListener("submit", async (event) => {
      event.preventDefault();

      if (!state.selectedVariant || !state.selectedVariant.available || button.disabled) {
        return;
      }

      setError(errorElement, "");

      button.disabled = true;
      button.classList.add("is-loading");

      if (label) {
        label.textContent = root.dataset.addingLabel || "Adding…";
      }

      try {
        const response = await fetch(root.dataset.cartAddUrl || "/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: new FormData(form)
        });

        const item = await response.json();

        if (!response.ok) {
          throw new Error(
            item.description ||
            item.message ||
            root.dataset.errorMessage ||
            "This product could not be added to the cart."
          );
        }

        button.classList.remove("is-loading");
        button.classList.add("is-added");

        if (label) {
          label.textContent = root.dataset.addedLabel || "Added ✓";
        }

        showToast(root, root.dataset.successMessage || "Added to cart");

        const cart = await fetchCart(root);
        updateCartCount(cart.item_count || 0);

        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart: cart }
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:refresh", {
            detail: { cart: cart }
          })
        );

        document.dispatchEvent(
          new CustomEvent("ozplug:cart-added", {
            detail: {
              item: item,
              cart: cart,
              sectionId: root.dataset.sectionId
            }
          })
        );

        window.setTimeout(() => {
          button.classList.remove("is-added");
          updateProductUI(root, state);
        }, 1300);
      } catch (error) {
        button.classList.remove("is-loading", "is-added");
        setError(
          errorElement,
          error instanceof Error
            ? error.message
            : root.dataset.errorMessage ||
              "This product could not be added to the cart."
        );
        updateProductUI(root, state);
      }
    });
  }

  function initRelatedForms(root) {
    const forms = Array.from(root.querySelectorAll("[data-oz-pdp-related-form]"));
    if (!forms.length) return;

    forms.forEach((form) => {
      if (form.hasAttribute("data-oz-related-initialized")) return;
      form.setAttribute("data-oz-related-initialized", "true");

      const button = form.querySelector("[data-oz-pdp-related-button]");
      const originalLabel = button ? button.textContent.trim() : "";

      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (!button || button.disabled) return;

        button.disabled = true;
        button.textContent = root.dataset.relatedAddingLabel || root.dataset.addingLabel || "Adding…";

        try {
          const response = await fetch(root.dataset.cartAddUrl || "/cart/add.js", {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
            body: new FormData(form)
          });

          const item = await response.json();

          if (!response.ok) {
            throw new Error(item.description || item.message || root.dataset.errorMessage || "This product could not be added to the cart.");
          }

          button.classList.add("is-added");
          button.textContent = root.dataset.relatedAddedLabel || root.dataset.addedLabel || "Added ✓";
          showToast(root, root.dataset.successMessage || "Added to cart");

          const cart = await fetchCart(root);
          updateCartCount(cart.item_count || 0);

          document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent("ozplug:cart-added", {
            detail: {
              item: item,
              cart: cart,
              sectionId: root.dataset.sectionId,
              source: "related-card"
            }
          }));

          window.setTimeout(() => {
            button.disabled = false;
            button.classList.remove("is-added");
            button.textContent = originalLabel || root.dataset.addLabel || "Add +";
          }, 1100);
        } catch (error) {
          button.disabled = false;
          button.classList.remove("is-added");
          button.textContent = originalLabel || root.dataset.addLabel || "Add +";
          showToast(root, error instanceof Error ? error.message : root.dataset.errorMessage || "This product could not be added to the cart.");
        }
      });
    });
  }

  async function fetchCart(root) {
    const response = await fetch(root.dataset.cartUrl || "/cart.js", {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    if (!response.ok) {
      throw new Error("Cart refresh failed.");
    }

    return response.json();
  }

  function updateCartCount(count) {
    document
      .querySelectorAll(
        "[data-oz-cart-count], [data-cart-count], .cart-count-bubble span[aria-hidden='true']"
      )
      .forEach((element) => {
        element.textContent = String(count);
        element.hidden = count < 1;
      });
  }

  function updateProductUI(root, state) {
    const variant = state.selectedVariant;
    const quantity = getQuantity(root);
    const variantInput = root.querySelector("[data-oz-pdp-variant-input]");
    const button = root.querySelector("[data-oz-pdp-add-button]");
    const buttonLabel = root.querySelector("[data-oz-pdp-add-label]");
    const priceElement = root.querySelector("[data-oz-pdp-price]");
    const compareElement = root.querySelector("[data-oz-pdp-compare-price]");
    const savingsElement = root.querySelector("[data-oz-pdp-savings]");
    const addPriceElement = root.querySelector("[data-oz-pdp-add-price]");
    const pointsElement = root.querySelector("[data-oz-pdp-points]");

    updateOptionCards(root, state);

    if (!variant) {
      if (button) {
        button.disabled = true;
      }

      if (buttonLabel) {
        buttonLabel.textContent = root.dataset.unavailableLabel || "Unavailable";
      }

      return;
    }

    if (variantInput) {
      variantInput.value = variant.id;
    }

    const totalPrice = variant.price * quantity;
    const totalComparePrice = variant.compare_at_price
      ? variant.compare_at_price * quantity
      : 0;

    if (priceElement) {
      priceElement.textContent = formatMoney(root, totalPrice);
    }

    if (addPriceElement) {
      addPriceElement.textContent = formatMoney(root, totalPrice);
    }

    if (compareElement) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareElement.hidden = false;
        compareElement.textContent = formatMoney(root, totalComparePrice);
      } else {
        compareElement.hidden = true;
      }
    }

    if (savingsElement) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        savingsElement.hidden = false;
        const savings = totalComparePrice - totalPrice;
        const label = root.dataset.savingsLabel || "YOU SAVE";
        savingsElement.textContent = `${label} ${formatMoney(root, savings)}`;
      } else {
        savingsElement.hidden = true;
      }
    }

    if (pointsElement) {
      const multiplier = Math.max(1, Number(root.dataset.pointsMultiplier) || 1);
      pointsElement.textContent = String(Math.floor((totalPrice / 100) * multiplier));
    }

    if (button) {
      button.disabled = !variant.available;
      button.classList.remove("is-loading");

      if (buttonLabel) {
        buttonLabel.textContent = variant.available
          ? root.dataset.addLabel || "Add to Cart"
          : root.dataset.soldOutLabel || "Sold out";
      }
    }

    if (variant.featured_media_id && typeof root.ozPdpSetActiveMedia === "function") {
      root.ozPdpSetActiveMedia(variant.featured_media_id);
    }

    updateUrl(variant);
  }

  function updateOptionCards(root, state) {
    const selectedOptions = state.selectedVariant
      ? state.selectedVariant.options
      : getSelectedOptions(root);

    root
      .querySelectorAll("[data-oz-pdp-option-group]")
      .forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        const selectedOptionText = root.querySelector(
          `[data-oz-pdp-selected-option="${optionIndex}"]`
        );

        if (selectedOptionText) {
          selectedOptionText.textContent = selectedOptions[optionIndex] || "";
        }

        group
          .querySelectorAll("[data-oz-pdp-option-card]")
          .forEach((card) => {
            const input = card.querySelector("[data-oz-pdp-option-input]");
            if (!input) return;

            const isActive = selectedOptions[optionIndex] === input.value;
            card.classList.toggle("is-active", isActive);

            const possibleOptions = selectedOptions.slice();
            possibleOptions[optionIndex] = input.value;

            const possibleVariant = findVariantByOptions(
              state.variants,
              possibleOptions
            );

            const disabled = !possibleVariant || !possibleVariant.available;
            card.classList.toggle("is-disabled", disabled);
          });
      });
  }

  function updateUrl(variant) {
    if (!variant || !variant.url || !window.history?.replaceState) return;

    window.history.replaceState({}, "", variant.url);
  }

  function setError(element, message) {
    if (!element) return;

    element.textContent = message;
    element.hidden = !message;
  }

  function showToast(root, message) {
    const toast = root.querySelector("[data-oz-pdp-toast]");
    const messageElement = root.querySelector("[data-oz-pdp-toast-message]");

    if (!toast || !messageElement) return;

    messageElement.textContent = message;
    toast.classList.add("is-visible");

    window.clearTimeout(root.ozPdpToastTimer);

    root.ozPdpToastTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
    }, 1800);
  }

  function formatMoney(root, cents) {
    const amount = Number(cents || 0) / 100;
    const moneyFormat = root.dataset.moneyFormat;

    if (window.Shopify && typeof window.Shopify.formatMoney === "function" && moneyFormat) {
      try {
        return window.Shopify.formatMoney(Number(cents || 0), moneyFormat);
      } catch (error) {
        // Fall through to Intl formatting.
      }
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: getShopCurrency(),
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return "$" + amount.toFixed(amount % 1 === 0 ? 0 : 2);
    }
  }

  function getShopCurrency() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }

    return "USD";
  }

  function initAll(context) {
    (context || document)
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
